// Free client-side wilaya tracking service
// Tracks visits by wilaya (Algerian region) using IP geolocation APIs
// Writes directly to Firestore (no Cloud Functions needed - completely free!)

import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';

// Algerian wilayas mapping (English names)
const algerianWilayas = {
  'Adrar': 'Adrar',
  'Chlef': 'Chlef',
  'Laghouat': 'Laghouat',
  'Oum El Bouaghi': 'Oum El Bouaghi',
  'Batna': 'Batna',
  'Béjaïa': 'Béjaïa',
  'Biskra': 'Biskra',
  'Béchar': 'Béchar',
  'Blida': 'Blida',
  'Bouira': 'Bouira',
  'Tamanrasset': 'Tamanrasset',
  'Tébessa': 'Tébessa',
  'Tlemcen': 'Tlemcen',
  'Tiaret': 'Tiaret',
  'Tizi Ouzou': 'Tizi Ouzou',
  'Algiers': 'Algiers',
  'Djelfa': 'Djelfa',
  'Jijel': 'Jijel',
  'Sétif': 'Sétif',
  'Saïda': 'Saïda',
  'Skikda': 'Skikda',
  'Sidi Bel Abbès': 'Sidi Bel Abbès',
  'Annaba': 'Annaba',
  'Guelma': 'Guelma',
  'Constantine': 'Constantine',
  'Médéa': 'Médéa',
  'Mostaganem': 'Mostaganem',
  'M\'Sila': 'M\'Sila',
  'Mascara': 'Mascara',
  'Ouargla': 'Ouargla',
  'Oran': 'Oran',
  'El Bayadh': 'El Bayadh',
  'Illizi': 'Illizi',
  'Bordj Bou Arréridj': 'Bordj Bou Arréridj',
  'Boumerdès': 'Boumerdès',
  'El Tarf': 'El Tarf',
  'Tindouf': 'Tindouf',
  'Tissemsilt': 'Tissemsilt',
  'El Oued': 'El Oued',
  'Khenchela': 'Khenchela',
  'Souk Ahras': 'Souk Ahras',
  'Tipaza': 'Tipaza',
  'Mila': 'Mila',
  'Aïn Defla': 'Aïn Defla',
  'Naâma': 'Naâma',
  'Aïn Témouchent': 'Aïn Témouchent',
  'Ghardaïa': 'Ghardaïa',
  'Relizane': 'Relizane',
  'Timimoun': 'Timimoun',
  'Bordj Badji Mokhtar': 'Bordj Badji Mokhtar',
  'Ouled Djellal': 'Ouled Djellal',
  'Béni Abbès': 'Béni Abbès',
  'In Salah': 'In Salah',
  'In Guezzam': 'In Guezzam',
  'Touggourt': 'Touggourt',
  'Djanet': 'Djanet',
  'El M\'Ghair': 'El M\'Ghair',
  'El Meniaa': 'El Meniaa'
};

// Helper function to normalize wilaya name
const normalizeWilaya = (regionName) => {
  if (!regionName) return null;
  
  // Direct match
  if (algerianWilayas[regionName]) {
    return algerianWilayas[regionName];
  }
  
  // Case-insensitive match
  const regionLower = regionName.toLowerCase();
  for (const [key, value] of Object.entries(algerianWilayas)) {
    if (key.toLowerCase() === regionLower) {
      return value;
    }
  }
  
  // Partial match (e.g., "Algiers Province" -> "Algiers")
  for (const [key, value] of Object.entries(algerianWilayas)) {
    if (regionLower.includes(key.toLowerCase()) || key.toLowerCase().includes(regionLower)) {
      return value;
    }
  }
  
  // Return as-is if it's a valid-looking name
  return regionName;
};

// Get wilaya from IP using free geolocation APIs
const getWilayaFromIP = async () => {
  try {
    // Primary: api.db-ip.com (free, no key, supports HTTPS/CORS)
    const response = await fetch('https://api.db-ip.com/v2/free/self', {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('db-ip.com response:', data);
      
      if (data.countryCode === 'DZ') {
        let wilaya = normalizeWilaya(data.stateProv);
        if (!wilaya && data.city) wilaya = normalizeWilaya(data.city);
        
        if (wilaya) {
          console.log('Wilaya detected from db-ip.com:', wilaya);
          return wilaya;
        }
        if (data.stateProv) return data.stateProv;
      }
    }
  } catch (error) {
    console.log('db-ip.com error:', error.message);
  }
  
  try {
    // Fallback 1: freeipapi.com (free, no key, supports HTTPS/CORS)
    const response = await fetch('https://freeipapi.com/api/json', {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('freeipapi.com response:', data);
      
      if (data.countryCode === 'DZ') {
        let wilaya = normalizeWilaya(data.regionName);
        if (!wilaya && data.cityName) wilaya = normalizeWilaya(data.cityName);
        
        if (wilaya) {
          console.log('Wilaya detected from freeipapi.com:', wilaya);
          return wilaya;
        }
        if (data.regionName) return data.regionName;
      }
    }
  } catch (error) {
    console.log('freeipapi.com error:', error.message);
  }
  
  try {
    // Fallback 2: ipapi.co (free, might block CORS on localhost but works in production)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
      },
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('ipapi.co response:', data);
      
      if (data.country_code === 'DZ') {
        let wilaya = normalizeWilaya(data.region);
        if (!wilaya && data.city) wilaya = normalizeWilaya(data.city);
        
        if (wilaya) {
          console.log('Wilaya detected from ipapi.co:', wilaya);
          return wilaya;
        }
        if (data.region) return data.region;
      }
    }
  } catch (error) {
    console.log('ipapi.co error:', error.message);
  }
  
  console.log('Could not determine wilaya from any API. This might be due to:');
  console.log('1. CORS restrictions (if deployed on HTTPS, some APIs may be blocked)');
  console.log('2. Visitor not from Algeria');
  console.log('3. API rate limits exceeded');
  console.log('4. Network connectivity issues');
  return null;
};

// Track visit by wilaya - increments counter in Firestore
export const trackWilayaVisit = async () => {
  try {
    // Check if we've already tracked this session (prevent duplicate calls)
    const hasTracked = sessionStorage.getItem('wilayaTracked');
    if (hasTracked) {
      return { success: false, message: 'Already tracked in this session' };
    }
    
    // Get wilaya from IP
    const wilaya = await getWilayaFromIP();
    
    if (!wilaya) {
      console.log('Could not determine wilaya from IP - visitor may not be from Algeria or API unavailable');
      return { success: false, message: 'Could not determine wilaya' };
    }
    
    console.log('Detected wilaya:', wilaya);
    
    // Update Firestore stats/wilayas document using increment
    const wilayasRef = doc(db, 'stats', 'wilayas');
    
    try {
      // Check if document exists
      const wilayasDoc = await getDoc(wilayasRef);
      
      if (wilayasDoc.exists()) {
        // Document exists - use increment for atomic update
        await updateDoc(wilayasRef, {
          [wilaya]: increment(1)
        });
      } else {
        // Document doesn't exist - create it
        await setDoc(wilayasRef, {
          [wilaya]: 1
        });
      }
      
      // Mark as tracked in this session
      sessionStorage.setItem('wilayaTracked', 'true');
      
      console.log(`Visit tracked for wilaya: ${wilaya}`);
      
      return { 
        success: true, 
        wilaya: wilaya,
        message: `Visit tracked for ${wilaya}` 
      };
    } catch (firestoreError) {
      console.error('Firestore error:', firestoreError);
      return { 
        success: false, 
        message: 'Error updating Firestore',
        error: firestoreError.message 
      };
    }
  } catch (error) {
    console.error('Error in trackWilayaVisit:', error);
    return { 
      success: false, 
      message: 'Error tracking visit',
      error: error.message 
    };
  }
};

// Get wilaya statistics from Firestore
export const getWilayaStats = async () => {
  try {
    const wilayasRef = doc(db, 'stats', 'wilayas');
    const wilayasDoc = await getDoc(wilayasRef);
    
    if (wilayasDoc.exists()) {
      return wilayasDoc.data();
    }
    
    return {};
  } catch (error) {
    console.error('Error getting wilaya stats:', error);
    return {};
  }
};

